// projectRouteAliases.tsx
//
// Maps the new Nav v2 /projects/:id/* paths to the existing pages in App.tsx.
//
// The ProjectSidebar (Agent B) uses /projects/:id/* paths per the Nav v2 IA
// blueprint (Level 2). Existing project pages still live at the original
// routes (/projects/:projectId/boq, /quality/ncrs, /dms/rfis, etc).
//
// To make the sidebar links work WITHOUT moving every page, we register
// /projects/:id/* paths as route aliases that render the same component as
// the original route. The bare /projects (list) page is intentionally NOT
// included — LayoutV2 keeps the Group sidebar there.
//
// Strategy mirrors groupRouteAliases.tsx:
//   - Project routes that have an existing page → alias to that page
//   - Project routes without a dedicated page → alias to closest match
//   - All routes are auth-protected (redirect to /login if not authed)
//
// Usage in App.tsx (Master must add manually — Agent Y does not edit App.tsx):
//   import { renderProjectRouteAliases } from '@/routes/projectRouteAliases'
//   ...
//   <Routes>
//     ...existing routes...
//     {renderProjectRouteAliases(isAuthenticated)}
//   </Routes>

import { ReactNode, lazy } from 'react'
import { Route, Navigate } from 'react-router-dom'

// Existing pages reused for /projects/:id/* aliases.
// All use the lazy() pattern to match the existing App.tsx style.
const ProjectDetailsPage = lazy(() => import('@/pages/ProjectDetailsPage'))
const BOQPage = lazy(() => import('@/pages/BOQPage'))
const QTODrawingsPage = lazy(() => import('@/pages/QTODrawingsPage'))
const QTOReviewPage = lazy(() => import('@/pages/QTOReviewPage'))
const CostEstimationListPage = lazy(() => import('@/pages/CostEstimationListPage'))
const DrawingsRegisterPage = lazy(() => import('@/pages/DrawingsRegisterPage'))
const RFIInboxPage = lazy(() => import('@/pages/RFIInboxPage'))
const SubmittalsTrackerPage = lazy(() => import('@/pages/SubmittalsTrackerPage'))
const TransmittalsListPage = lazy(() => import('@/pages/TransmittalsListPage'))
const DMSLettersInboxPage = lazy(() => import('@/pages/DMSLettersInboxPage'))
const ApprovalsInboxPage = lazy(() => import('@/pages/ApprovalsInboxPage'))
const QualityDashboardPage = lazy(() => import('@/pages/QualityDashboardPage'))
const QualityInspectionsPage = lazy(() => import('@/pages/QualityInspectionsPage'))
const NCRsPage = lazy(() => import('@/pages/NCRsPage'))
const PunchListPage = lazy(() => import('@/pages/PunchListPage'))
const HSEDashboardPage = lazy(() => import('@/pages/HSEDashboardPage'))
const VariationOrdersPage = lazy(() => import('@/pages/VariationOrdersPage'))
const FieldReportsListPage = lazy(() => import('@/pages/FieldReportsListPage'))
const SchedulePage = lazy(() => import('@/pages/SchedulePage'))
const SubcontractsListPage = lazy(() => import('@/pages/SubcontractsListPage'))
const PurchaseOrdersPage = lazy(() => import('@/pages/PurchaseOrdersPage'))
// B31 Lane L4 / PO-GROUP-SCOPE-01 -- see the aliases below.
const PurchaseOrderDetailsPage = lazy(() => import('@/pages/PurchaseOrderDetailsPage'))
// SI-DETAIL-GROUP-ROUTE-01 (B31 Lane L2)
const InvoiceDetailsPage = lazy(() => import('@/pages/InvoiceDetailsPage'))
const SupplierInvoicesPage = lazy(() => import('@/pages/SupplierInvoicesPage'))
// === B29 LANE D (ROUTE-RECEIPTS-GRN-01) BEGIN ===
// Goods Received Notes. Procurement/GRNsPage is the API-backed page
// (procurementApi.listGRNs); pages/Materials/GRN.tsx renders a hardcoded
// ROWS[] mock, so it must NOT be what 'استلام المواد' opens.
const ProcurementGRNsPage = lazy(() => import('@/pages/Procurement/GRNsPage'))
// === B29 LANE D END ===
const ProjectPaymentsPage = lazy(() => import('@/pages/ProjectPaymentsPage'))
const ProjectCashFlowPage = lazy(() => import('@/pages/ProjectCashFlowPage'))
const AgingReportPage = lazy(() => import('@/pages/AgingReportPage'))
const TeamPage = lazy(() => import('@/pages/TeamPage'))
const SettingsPage = lazy(() => import('@/pages/SettingsPage'))
const SustainabilityDashboardPage = lazy(() => import('@/pages/SustainabilityDashboardPage'))
const SustainabilityCertificationsPage = lazy(() => import('@/pages/SustainabilityCertificationsPage'))
const SustainabilityCarbonPage = lazy(() => import('@/pages/SustainabilityCarbonPage'))
const SustainabilityResourcesPage = lazy(() => import('@/pages/SustainabilityResourcesPage'))
const MaterialRequestsPage = lazy(() => import('@/pages/MaterialRequestsPage'))
const MaterialConsumptionPage = lazy(() => import('@/pages/MaterialConsumptionPage'))
const MaterialTheftAlertsPage = lazy(() => import('@/pages/MaterialTheftAlertsPage'))
// === Wave 8/9 Agent E8 BEGIN ===
const E8ScenariosPage = lazy(() => import('@/pages/Estimation/ScenariosPage'))
// === Wave 8/9 Agent E8 END ===

// Type for the alias map
interface RouteAlias {
  path: string
  element: ReactNode
}

/**
 * Build the list of route aliases for /projects/:id/* paths.
 * Each alias renders an existing page when authenticated.
 */
export function buildProjectRouteAliases(isAuthenticated: boolean): RouteAlias[] {
  const protect = (Page: React.ComponentType): ReactNode =>
    isAuthenticated ? <Page /> : <Navigate to="/login" replace />

  return [
    // Project home (sidebar's "Project Dashboard" entry)
    { path: '/projects/:projectId/home', element: protect(ProjectDetailsPage) },
    // Short-form section landing pages — let users type the bare suffix
    // without 404ing when they bookmark or share a stripped URL.
    { path: '/projects/:projectId/information', element: protect(ProjectDetailsPage) },
    { path: '/projects/:projectId/qto', element: protect(QTODrawingsPage) },
    { path: '/projects/:projectId/estimation', element: protect(CostEstimationListPage) },
    { path: '/projects/:projectId/materials', element: protect(MaterialRequestsPage) },
    { path: '/projects/:projectId/ipcs', element: protect(ProjectPaymentsPage) },
    { path: '/projects/:projectId/documents', element: protect(DrawingsRegisterPage) },

    // Pre-Construction
    { path: '/projects/:projectId/pre-construction/qto', element: protect(QTODrawingsPage) },
    { path: '/projects/:projectId/pre-construction/qto/review', element: protect(QTOReviewPage) },
    { path: '/projects/:projectId/pre-construction/estimation', element: protect(CostEstimationListPage) },
    // === Wave 8/9 Agent E8 BEGIN ===
    { path: '/projects/:projectId/pre-construction/scenarios', element: protect(E8ScenariosPage) },
    // === Wave 8/9 Agent E8 END ===
    { path: '/projects/:projectId/pre-construction/boq', element: protect(BOQPage) },

    // Documents (DMS)
    { path: '/projects/:projectId/documents/drawings', element: protect(DrawingsRegisterPage) },
    { path: '/projects/:projectId/documents/rfis', element: protect(RFIInboxPage) },
    { path: '/projects/:projectId/documents/submittals', element: protect(SubmittalsTrackerPage) },
    { path: '/projects/:projectId/documents/transmittals', element: protect(TransmittalsListPage) },
    { path: '/projects/:projectId/documents/correspondence', element: protect(DMSLettersInboxPage) },
    { path: '/projects/:projectId/documents/approvals', element: protect(ApprovalsInboxPage) },

    // Quality & HSE
    { path: '/projects/:projectId/quality/dashboard', element: protect(QualityDashboardPage) },
    { path: '/projects/:projectId/quality/inspections', element: protect(QualityInspectionsPage) },
    { path: '/projects/:projectId/quality/ncrs', element: protect(NCRsPage) },
    { path: '/projects/:projectId/quality/snag', element: protect(PunchListPage) },
    { path: '/projects/:projectId/quality/hse', element: protect(HSEDashboardPage) },

    // Change Management
    { path: '/projects/:projectId/changes/orders', element: protect(VariationOrdersPage) },
    { path: '/projects/:projectId/changes/vos', element: protect(VariationOrdersPage) },
    { path: '/projects/:projectId/changes/pending', element: protect(ApprovalsInboxPage) },

    // Execution & Site
    { path: '/projects/:projectId/field/daily-reports', element: protect(FieldReportsListPage) },
    { path: '/projects/:projectId/field/photos', element: protect(FieldReportsListPage) },
    { path: '/projects/:projectId/field/activities', element: protect(SchedulePage) },
    { path: '/projects/:projectId/field/schedule', element: protect(SchedulePage) },

    // Field Reports (top-level section)
    { path: '/projects/:projectId/field-reports', element: protect(FieldReportsListPage) },

    // Schedule (top-level alias — fixes blank Schedule button bug)
    { path: '/projects/:projectId/schedule', element: protect(SchedulePage) },

    // Subcontractors
    { path: '/projects/:projectId/subcontractors', element: protect(SubcontractsListPage) },

    // Procurement
    { path: '/projects/:projectId/procurement/pos', element: protect(PurchaseOrdersPage) },
    // === B31 LANE L4 (PO-GROUP-SCOPE-01 / DEAD-CREATE-ROUTES-01) BEGIN ===
    // Same leak as the group scope: the list's row click used to jump to the
    // absolute `/purchase-orders/:id`, dropping the user out of the project.
    // The list now builds its targets from the live pathname
    // (pages/Procurement/procurementScope.ts), so they must exist here too.
    // `:poId` is the param name PurchaseOrderDetailsPage.tsx:62 reads.
    { path: '/projects/:projectId/procurement/pos/new', element: protect(PurchaseOrdersPage) },
    { path: '/projects/:projectId/procurement/pos/:poId', element: protect(PurchaseOrderDetailsPage) },
    // === B31 LANE L4 END ===
    { path: '/projects/:projectId/procurement/supplier-invoices', element: protect(SupplierInvoicesPage) },
    // === B31 LANE L2 (SI-DETAIL-GROUP-ROUTE-01) BEGIN ===
    { path: '/projects/:projectId/procurement/supplier-invoices/new', element: protect(SupplierInvoicesPage) },
    // `:invoiceId` is the param name InvoiceDetailsPage.tsx:266 reads.
    { path: '/projects/:projectId/procurement/supplier-invoices/:invoiceId', element: protect(InvoiceDetailsPage) },
    // === B31 LANE L2 END ===
    // === B29 LANE D (ROUTE-RECEIPTS-GRN-01) BEGIN ===
    // Was pointing at SupplierInvoicesPage -- the ProjectSidebar entry
    // 'استلام المواد' / 'Material Receipts' means goods receipts, not invoices.
    { path: '/projects/:projectId/procurement/receipts', element: protect(ProcurementGRNsPage) },
    // === B29 LANE D END ===

    // Billing & Payments
    { path: '/projects/:projectId/billing/ipcs', element: protect(ProjectPaymentsPage) },
    { path: '/projects/:projectId/billing/milestones', element: protect(ProjectPaymentsPage) },
    { path: '/projects/:projectId/billing/client-invoices', element: protect(ProjectPaymentsPage) },
    { path: '/projects/:projectId/billing/cash-flow', element: protect(ProjectCashFlowPage) },
    { path: '/projects/:projectId/billing/aging', element: protect(AgingReportPage) },

    // Materials
    { path: '/projects/:projectId/materials/requests', element: protect(MaterialRequestsPage) },
    { path: '/projects/:projectId/materials/consumption', element: protect(MaterialConsumptionPage) },
    { path: '/projects/:projectId/materials/theft-alerts', element: protect(MaterialTheftAlertsPage) },

    // Sustainability
    { path: '/projects/:projectId/sustainability/dashboard', element: protect(SustainabilityDashboardPage) },
    { path: '/projects/:projectId/sustainability/certifications', element: protect(SustainabilityCertificationsPage) },
    { path: '/projects/:projectId/sustainability/carbon', element: protect(SustainabilityCarbonPage) },
    { path: '/projects/:projectId/sustainability/resources', element: protect(SustainabilityResourcesPage) },

    // Project Team
    { path: '/projects/:projectId/team', element: protect(TeamPage) },

    // Project Settings
    { path: '/projects/:projectId/settings', element: protect(SettingsPage) },
  ]
}

/**
 * Render all /projects/:id/* alias routes as <Route> elements.
 * Drop into the <Routes> tree in App.tsx.
 */
export function renderProjectRouteAliases(isAuthenticated: boolean): ReactNode {
  const aliases = buildProjectRouteAliases(isAuthenticated)
  return aliases.map(({ path, element }) => (
    <Route key={path} path={path} element={element} />
  ))
}
